import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Resets the window scroll on every SPA route change so navigating to a
// new page never lands the user mid- or end-page (a common React Router
// gotcha because pushState doesn't trigger a real browser navigation).
//
// Browser back/forward (history "POP") is intentionally exempt — we want
// the platform's scroll-restoration to bring the user back to where they
// were when they pressed Back. Our own forward navigation always starts
// at the top.
//
// `behavior: "instant"` is deliberate: smooth scroll on route change
// causes motion sickness for users with vestibular sensitivity and looks
// worse than a hard reset. This is also what `prefers-reduced-motion`
// users would expect.
//
// Links with a hash (the nav's "/#how", "/#pricing", "/#faq") scroll to
// that section instead, retrying briefly while the target page renders.
export function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType === "POP" || hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, navType, hash]);
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let tries = 0;
    let timer = 0;
    const attempt = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "start" });
        return;
      }
      if (tries++ < 40) timer = window.setTimeout(attempt, 50);
    };
    attempt();
    return () => window.clearTimeout(timer);
  }, [hash, key]);
  return null;
}
